<template>
  <vs-popup :active.sync="isActive" title="" @close="close">
    <div class="state-change-popup">

      <button class="popup-close" @click="close">✕</button>

      <div v-if="!isProcessing" class="popup-body">
        <div :class="['popup-icon', isCanceling ? 'icon-danger' : 'icon-success']">
          <feather-icon :icon="isCanceling ? 'AlertTriangleIcon' : 'CheckCircleIcon'" svgClasses="w-10 h-10" />
        </div>
        <h3 class="popup-title">{{ isCanceling ? 'Anular cobro' : 'Confirmar cobro' }}</h3>
        <p class="message">{{ popupMessage }}</p>

        <div v-if="isCanceling" class="cancel-comment">
          <label class="cancel-comment-label">Comentario</label>
          <vs-textarea
            v-model="cancellationComment"
            :rows="3"
            maxlength="1000"
            placeholder="Indique el motivo de anulación, campo obligatorio"
          />
          <p v-if="cancellationComment.trim().length > 0 && cancellationComment.trim().length < 10" class="cancel-comment-hint">
            Mínimo 10 caracteres.
          </p>
        </div>
      </div>

      <div v-if="isProcessing" class="processing">
        <vs-loading></vs-loading>
        <p>Procesando cambio de estado...</p>
      </div>

      <div v-if="errorMessage" class="error-message">
        <vs-alert color="danger" class="mb-3">{{ errorMessage }}</vs-alert>
      </div>

      <div v-if="!isProcessing" class="popup-actions">
        <vs-button type="border" @click="close">Cancelar</vs-button>
        <vs-button :color="isCanceling ? 'danger' : 'primary'" @click="handleConfirm" :disabled="!canProceed">
          {{ isCanceling ? 'Sí, anular' : 'Sí, cobrar' }}
        </vs-button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import appConfig from '@/../appConfig.js'
import { CHARGE_STATUS } from '@/constants/billingAndCharges'

export default {
  name: 'ChargesStateChangePopup',
  props: {
    popupActive: {
      type: Boolean,
      default: false
    },
    selectedRows: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isActive: this.popupActive,
      isProcessing: false,
      errorMessage: '',
      newStatus: null,
      cancellationComment: ''
    }
  },
  computed: {
    popupMessage() {
      if (!this.selectedRows || this.selectedRows.length === 0) return ''
      const firstRow = this.selectedRows[0]
      const currentStatus = firstRow.charge_status
      if (currentStatus === CHARGE_STATUS.COLLECTED) {
        return `¿Estás seguro de que deseas anular el cobro de ${this.selectedRows.length} cierre(s)?\n\nEsta acción revierte el estado a "No cobrado".`
      } else {
        return `¿Estás seguro de que deseas marcar ${this.selectedRows.length} cierre(s) como cobrado?\n\nEsta acción bloqueará la anulación del cobro hasta que lo hagas explícitamente.`
      }
    },
    canProceed() {
      if (!this.selectedRows || this.selectedRows.length === 0 || this.isProcessing) return false
      // Cancellation requires a reason of at least 10 characters
      if (this.isCanceling) return this.cancellationComment.trim().length >= 10
      return true
    },
    isCanceling() {
      if (!this.selectedRows || this.selectedRows.length === 0) return false
      return this.selectedRows[0].charge_status === CHARGE_STATUS.COLLECTED
    }
  },
  watch: {
    popupActive(val) {
      this.isActive = val
      if (val) {
        this.errorMessage = ''
        this.cancellationComment = ''
      }
    },
    isActive(val) {
      this.$emit('update:popupActive', val)
    }
  },
  methods: {
    async handleConfirm() {
      this.isProcessing = true
      this.errorMessage = ''
      try {
        const firstRow = this.selectedRows[0]
        const currentStatus = firstRow.charge_status
        const newStatus = currentStatus === CHARGE_STATUS.COLLECTED ? 'canceled' : 'collected'
        const body = { status: newStatus }
        if (newStatus === 'canceled') {
          body.cancellation_comment = this.cancellationComment.trim()
        }
        const promises = this.selectedRows.map(row => {
          const billingId = (row.billing && row.billing.id) || row.id
          return this.$http.patch(
            `${appConfig.apiUrl}/sales-closure-charges/${billingId}`,
            body
          )
        })
        await Promise.all(promises)
        this.$vs.notify({
          title: 'Éxito',
          text: `Se actualizaron ${this.selectedRows.length} cierre(s) correctamente`,
          color: 'success'
        })
        this.$emit('refresh')
        this.close()
      } catch (error) {
        console.error('Error updating charge status:', error)
        if (error.response && error.response.status === 422) {
          this.errorMessage = (error.response.data && error.response.data.message) || 'No se puede realizar la acción'
        } else {
          this.errorMessage = 'Error al actualizar el estado. Por favor, intenta de nuevo.'
        }
      } finally {
        this.isProcessing = false
      }
    },
    close() {
      this.isActive = false
      this.errorMessage = ''
    }
  }
}
</script>

<style lang="scss">
@import '@/views/css/billing-charges-state-popup.scss';
</style>

<style scoped>
::v-deep .vs-popup--close {
  display: none !important;
}

.cancel-comment {
  width: 100%;
  text-align: left;
  margin-top: 12px;
}

.cancel-comment-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.cancel-comment-hint {
  color: #EA5455;
  font-size: 12px;
  margin-top: 4px;
}
</style>
